/**
 * Skinned GLB BODY MODELS + clothing-piece templates (the assets built by
 * client/test/build-male.mts into client/public/models/male/).
 *
 * Every piece is authored in the avatar's NATURAL rest space (Manny rest pose, feet at origin,
 * +Z forward) with skinIndex values that index MANNY_RIG order directly — i.e. the avatar's own
 * skeleton.bones order — so at runtime a piece binds straight to the live skeleton
 * (mesh.bind(avatar.skeleton, suitBindMatrix)) with no joint remapping.
 *
 * Templates are module-cached and SHARED across all avatars: the same BufferGeometry object backs
 * every avatar's SkinnedMesh (GPU buffers uploaded once). The one exception is the BODY, whose
 * per-avatar geometry shares the template's vertex attributes but owns its INDEX buffer. Runtime
 * clothing occlusion is reversible index compaction: coarse regions remain for cosmetic layers
 * such as hair, while garments use overlapping face-level masks generated from actual coverage.
 *
 * Adding a body model (e.g. female): drop raw exports in client/model-library/<id>/, add a piece
 * config in build-male.mts (or its future generalization), run the build, then register the
 * asset paths in BODY_MODELS below + the id in shared BODY_IDS.
 */
import * as THREE from "three";
import { CHARACTER_ASSETS } from "./characterAssets.generated";
import { MALE_RIG } from "./maleRig";
import { FEMALE_RIG } from "./femaleRig";
import { rigProfile, validateRigSkin } from "./rigProfile";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

/** Hideable base-body regions (tagged per triangle range in the body GLB). */
export type BodyRegion =
  | "hair" | "face" | "head" | "neck" | "collar" | "torso" | "waist" | "hips"
  | "arm_l" | "arm_r" | "hand_l" | "hand_r"
  | "thigh_l" | "thigh_r" | "calf_upper_l" | "calf_upper_r" | "calf_lower_l" | "calf_lower_r"
  | "foot_l" | "foot_r";

/** Material groups a piece's primitives are split into (mapped to avatar materials). */
export type PieceGroup = "skin" | "colored" | "accent";

export interface PiecePrim {
  group: PieceGroup;
  /** Shared template geometry — never dispose, never mutate (body clones its index). */
  geometry: THREE.BufferGeometry;
  /** Region -> [start, count] index ranges (body prims only). */
  regions?: Partial<Record<BodyRegion, [number, number]>>;
  /** Garment id -> body triangle ordinals safely enclosed by that garment. */
  bodyCoverage?: readonly { vertices: number; triangles: number; hidden: number[] }[];
  occlusionMasks?: Readonly<Record<string, Uint32Array>>;
  /** This prim belongs to a HAIR piece (whole-piece hair paint target). */
  hairPiece?: boolean;
  /** Lazily computed appearance paint targets (appearanceVerts). */
  _appearance?: AppearanceVerts;
}

/** Vertex index sets for runtime appearance PAINTING (2026-07-31 colour axes):
 *  hair + iris colours are per-avatar COLOR_0 rewrites — no material split, no
 *  region remap. Hair verts come from the body's `hair` region (the male base
 *  hair) or, on a hair piece, every DARK vert (a few scalp-tone verts are
 *  authored skin and stay); iris verts are the chromatic mid-dark COLOR_0
 *  bucket, only on prims that also carry the light-grey sclera bucket (the
 *  brow/lash prims share the dark palette but have no sclera). */
export interface AppearanceVerts { hair: Uint32Array; eye: Uint32Array; brow: Uint32Array }

export function appearanceVerts(prim: PiecePrim): AppearanceVerts {
  if (prim._appearance) return prim._appearance;
  // Oral interiors contain pale enamel and dark tissue, just as eye primitives
  // contain sclera and iris. Explicit authoring metadata precedes color inference.
  if (prim.geometry.userData.appearance === "fixed") {
    return prim._appearance = { hair: new Uint32Array(), eye: new Uint32Array(), brow: new Uint32Array() };
  }
  const hair = new Set<number>();
  const eye = new Set<number>();
  const brow = new Set<number>();
  const color = prim.geometry.getAttribute("color") as THREE.BufferAttribute | undefined;
  const norm = color?.normalized === true;
  const ch = (i: number, c: number): number => {
    const v = (color!.array as ArrayLike<number>)[i * color!.itemSize + c];
    return norm ? v / 255 : v;
  };
  if (prim.hairPiece && color) {
    for (let v = 0; v < color.count; v++) {
      if (prim.geometry.userData.appearance === "hair" || Math.max(ch(v, 0), ch(v, 1), ch(v, 2)) < 0.5) hair.add(v);
    }
  } else if (prim.regions?.hair && prim.geometry.index) {
    const [start, count] = prim.regions.hair;
    const idx = prim.geometry.index.array as ArrayLike<number>;
    for (let i = start; i < start + count; i++) hair.add(idx[i]);
  }
  if (!prim.hairPiece && color) {
    // Eye-bearing prims carry the near-white sclera; find it first.
    let sclera = 0;
    for (let v = 0; v < color.count; v++) {
      const r = ch(v, 0), g = ch(v, 1), b = ch(v, 2);
      if (r > 0.6 && g > 0.6 && b > 0.6) sclera++;
      if (sclera > 40) break;
    }
    if (sclera > 40) {
      for (let v = 0; v < color.count; v++) {
        const m = Math.max(ch(v, 0), ch(v, 1), ch(v, 2));
        // Extracted heads can combine scalp hair and eyeballs in one primitive.
        // The authored hair region takes priority over the iris color bucket.
        if (!hair.has(v) && m > 0.03 && m < 0.5) eye.add(v);
      }
    } else {
      // BROW/LASH prims (2026-08-10, user call): all-dark face prims with no
      // sclera. Their OWN paint set — brows track the chosen hair colour but a
      // shade darker (avatar.ts BROW_DARKEN), the character-art trick that keeps
      // a brow line on pale hair. The rule is safe: body/suit prims carry big
      // bright regions and never read as ≥90% dark.
      let dark = 0;
      for (let v = 0; v < color.count; v++) {
        if (Math.max(ch(v, 0), ch(v, 1), ch(v, 2)) < 0.5) dark++;
      }
      if (dark > 0 && dark >= color.count * 0.9) {
        for (let v = 0; v < color.count; v++) {
          if (Math.max(ch(v, 0), ch(v, 1), ch(v, 2)) < 0.5) brow.add(v);
        }
      }
    }
  }
  prim._appearance = { hair: Uint32Array.from(hair), eye: Uint32Array.from(eye), brow: Uint32Array.from(brow) };
  return prim._appearance;
}

export interface PieceTemplate { prims: PiecePrim[] }

/** GLB body model registry: base body + always-on extras (hair rides the "hair" cover region).
 *  These are the ONLY bodies — there is no procedural fallback. Unknown ids resolve to "male"
 *  (avatar.ts setBodyModel), templates are prefetched at boot, and an avatar simply has no
 *  visible body until its template lands.
 *
 *  HOLOGRAPHIC ("block") is the SAME mesh as EXPLORER, drawn with the holo material — so its bones,
 *  proportions and silhouette are identical to the real body by construction, and the clothing
 *  covers work on it. */
export const BODY_MODELS: Record<string, { body: string; head?: string; hair?: string; holo?: boolean }> = {
  male: { body: "/models/male/body_builds.glb", head: "/models/male/head_base.glb", hair: "/models/male/hair_spiky.glb" },
  // hair "none" reveals the built dome crown (build-female buildShipCaps) — a proper bald
  // look, so the hair is removable exactly like the male's.
  female: { body: "/models/female/body_builds.glb", head: "/models/female/head_base.glb", hair: "/models/female/hair_long.glb" },
  block: { body: "/models/male/body.glb", hair: "/models/male/hair_spiky.glb", holo: true },
};

const GROUPS: readonly PieceGroup[] = ["skin", "colored", "accent"];

let loader: GLTFLoader | null = null;
const cache = new Map<string, Promise<PieceTemplate | null>>();
/** Load (once) a piece template. Resolves null on failure — callers just skip the piece. */
export function loadPiece(url: string): Promise<PieceTemplate | null> {
  let p = cache.get(url);
  if (!p) {
    p = (async () => {
      const asset = CHARACTER_ASSETS[url];
      if (!asset) throw new Error(`Unregistered character asset: ${url}; rebuild the character manifest`);
      const rig = asset.rig === "female" ? FEMALE_RIG : MALE_RIG;
      if (asset.fingerprint !== rigProfile(rig).fingerprint) throw new Error(`Stale character manifest for ${url}`);
      const response = await fetch(`${url}?v=${asset.hash}`, { integrity: asset.integrity });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${url}`);
      const gltf = await (loader ??= new GLTFLoader()).parseAsync(await response.arrayBuffer(), url.slice(0, url.lastIndexOf("/") + 1));
      // Validate every primitive before publishing any shared geometry.
      try {
        gltf.scene.traverse(object => {
          if (object instanceof THREE.Mesh) {
            if (!(object instanceof THREE.SkinnedMesh)) throw new Error(`${url}: unskinned character primitive`);
            validateRigSkin(object, rig);
            object.geometry.userData.rigFingerprint = asset.fingerprint;
          }
        });
      } catch (error) {
        gltf.scene.traverse(object => {
          if (object instanceof THREE.Mesh) {
            object.geometry.dispose();
            for (const material of Array.isArray(object.material) ? object.material : [object.material]) material.dispose();
          }
        });
        throw error;
      }
      return gltf;
    })().then((gltf) => {
        const prims: PiecePrim[] = [];
        gltf.scene.traverse((o) => {
          if (!(o instanceof THREE.Mesh)) return;
          const group = (GROUPS as readonly string[]).includes((o.material as THREE.Material).name)
            ? ((o.material as THREE.Material).name as PieceGroup)
            : "colored";
          // Prefer primitive regions: brows and oral interiors share a material
          // group but can occupy different regions of the same glTF mesh.
          const regions =
            (o.geometry.userData?.regions as PiecePrim["regions"]) ??
            (o.userData?.regions?.[group] as PiecePrim["regions"]);
          const rawMasks =
            (o.userData?.occlusionMasks?.[group] as Record<string, number[]> | undefined) ??
            (o.geometry.userData?.occlusionMasks as Record<string, number[]> | undefined);
          const occlusionMasks = rawMasks
            ? Object.fromEntries(Object.entries(rawMasks).map(([id, tris]) => [id, Uint32Array.from(tris)]))
            : undefined;
          // face-morph prims: name the morph attributes from the GLB's targetNames so
          // Mesh.updateMorphTargets() builds the name->index dictionary on avatar meshes
          const targetNames = o.userData?.targetNames as string[] | undefined;
          if (targetNames && o.geometry.morphAttributes.position) {
            o.geometry.morphAttributes.position.forEach((a: THREE.BufferAttribute, i: number) => { a.name = targetNames[i] ?? String(i); });
          }
          (o.material as THREE.Material).dispose(); // template meshes are never rendered directly
          o.geometry.userData.sharedTemplate = true; // shared across avatars: never dispose per avatar
          prims.push({ group, geometry: o.geometry, regions, occlusionMasks, bodyCoverage: o.geometry.userData.bodyCoverage, hairPiece: /\/hair_/.test(url) });
        });
        return prims.length ? { prims } : null;
      })
      .catch((e) => {
        console.warn(`[bodyModels] "${url}" failed to load:`, e instanceof Error ? e.message : e);
        cache.delete(url); // a corrected deployment or transient failure can be retried
        return null;
      });
    cache.set(url, p);
  }
  return p;
}

/**
 * Build a per-avatar body geometry: SHARES the template's vertex attributes, OWNS a full-size
 * index whose front section is rewritten by applyCovers (drawRange trims the rest) — so cover
 * changes never reallocate GPU buffers.
 */
export function instanceBodyGeometry(tpl: PiecePrim, ownColor = false): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.userData.appearance = tpl.geometry.userData.appearance;
  for (const [name, attr] of Object.entries(tpl.geometry.attributes)) geo.setAttribute(name, attr as THREE.BufferAttribute);
  // face-morph prims: share the template's delta buffers (influences live on the MESH, so
  // per-avatar faces stay independent even on shared morph attributes)
  geo.morphAttributes = tpl.geometry.morphAttributes;
  geo.morphTargetsRelative = tpl.geometry.morphTargetsRelative;
  const src = tpl.geometry.index!;
  const own = new THREE.BufferAttribute((src.array as Uint32Array).slice(), 1);
  geo.setIndex(own);
  // Appearance painting (2026-07-31 colour axes): a per-avatar COLOR_0 clone so
  // hair/iris rewrites never touch the shared template buffers.
  if (ownColor) {
    const c = tpl.geometry.getAttribute("color") as THREE.BufferAttribute | undefined;
    if (c) {
      const clone = new THREE.BufferAttribute((c.array as Uint8Array | Float32Array).slice(), c.itemSize, c.normalized);
      geo.setAttribute("color", clone);
      geo.userData.ownAttrs = ["color"];
    }
  }
  geo.boundingBox = tpl.geometry.boundingBox;
  geo.boundingSphere = tpl.geometry.boundingSphere;
  // dispose() must free only the per-avatar index (+ owned attrs) — the rest are the template's
  geo.userData.sharedTemplateAttrs = true;
  return geo;
}

/** Include seam coverage only when both corresponding garments have mounted. */
export function clothingCoverage(meshes: readonly {geometry: THREE.BufferGeometry}[]) {
  const data = meshes.map(m => m.geometry.userData);
  const mounted = new Set(data.map(d => `${d.outfit}/${d.piece}`));
  return data.flatMap(d => [
    ...(d.bodyCoverage ?? []),
    ...(d.bodyCoverageWith ?? []).flatMap((pair: {outfit: string; piece: string; masks: {vertices: number; triangles: number; hidden: number[]}[]}) =>
      mounted.has(`${pair.outfit}/${pair.piece}`) ? pair.masks : []),
  ]);
}

/** Compact visible triangles to the front of the owned index; drawRange trims the rest. Garment
 * masks overlap safely and never mutate or destroy the canonical body template. */
export function applyCovers(
  geo: THREE.BufferGeometry,
  tpl: PiecePrim,
  hidden: ReadonlySet<BodyRegion>,
  garmentMasks: ReadonlySet<string> = new Set(),
  clothCoverage: readonly { vertices: number; triangles: number; hidden: number[] }[] = [],
): void {
  const src = tpl.geometry.index!.array as Uint32Array;
  const dst = geo.index!.array as Uint32Array;
  const visible = new Uint8Array(src.length / 3).fill(1);
  if (tpl.regions) {
    for (const [region, [start, count]] of Object.entries(tpl.regions) as [BodyRegion, [number, number]][]) {
      if (!hidden.has(region)) continue;
      visible.fill(0, start / 3, (start + count) / 3);
    }
  }
  if (tpl.occlusionMasks) for (const id of garmentMasks) {
    const tris = tpl.occlusionMasks[id];
    if (tris) for (const tri of tris) visible[tri] = 0;
  }
  for (const mask of clothCoverage) if (mask.vertices === tpl.geometry.attributes.position.count && mask.triangles === src.length / 3) {
    for (const tri of mask.hidden) visible[tri] = 0;
  }
  let n = 0;
  for (let tri = 0; tri < visible.length; tri++) {
    if (!visible[tri]) continue;
    const start = tri * 3;
    dst[n++] = src[start]; dst[n++] = src[start + 1]; dst[n++] = src[start + 2];
  }
  geo.index!.needsUpdate = true;
  geo.setDrawRange(0, n);
}

/** Dispose an avatar-owned mesh geometry without nuking shared template GPU buffers. */
export function disposeAvatarGeometry(geo: THREE.BufferGeometry): void {
  if (geo.userData.sharedTemplate) return; // template-owned: shared across avatars, keep
  if (geo.userData.sharedTemplateAttrs) {
    // strip shared attributes so dispose() frees only the per-avatar index buffer
    // — but KEEP avatar-owned attributes (the painted COLOR_0 clone) so their
    // GPU buffers are freed with the geometry.
    const own = new Set((geo.userData.ownAttrs as string[] | undefined) ?? []);
    const kept: Record<string, THREE.BufferAttribute> = {};
    for (const name of own) {
      const a = geo.getAttribute(name) as THREE.BufferAttribute | undefined;
      if (a) kept[name] = a;
    }
    (geo as unknown as { attributes: Record<string, unknown> }).attributes = kept;
    geo.morphAttributes = {};
  }
  geo.dispose();
}
